import { Locales } from '@griply/types';

// More permissive pattern for words inside properties like goals, tags, and people.
// It allows almost any character, including numbers and colons at the start.
export const propertyWordPart =
  '[\\p{L}\\p{N}\\p{Extended_Pictographic}_&.+-:@!#]+'; // Allow '#' in property names.

// Define different "greedy" levels for parsing property names.
// A goal is the most greedy: it can contain tags and assignees. It only stops for a new goal or a terminator.
export const subsequentCharsForGoal = '(?:(?!\\s~|\u200B).)*';
// A tag can contain an assignee, but stops for a new goal or tag.
export const subsequentCharsForTag = '(?:(?!\\s[#~]|\u200B).)*';
// A person is the least greedy and stops for any new property.
export const subsequentCharsForPerson = '(?:(?!\\s[#@~]|\u200B).)*';

export const goalSubPattern = `${propertyWordPart}${subsequentCharsForGoal}`;
export const tagSubPattern = `${propertyWordPart}${subsequentCharsForTag}`;
export const personSubPattern = `${propertyWordPart}${subsequentCharsForPerson}`;

export const goalRegex =
  /(~[a-zA-Z-][a-zA-Z0-9-]*(?: [a-zA-Z-][a-zA-Z0-9-]*)*)/g;
export const tagPattern = '#[a-zA-Z-][a-zA-Z0-9-]*(?: [a-zA-Z-][a-zA-Z0-9-]*)*';
export const personPattern =
  '@[a-zA-Z-][a-zA-Z0-9-]*(?: [a-zA-Z-][a-zA-Z0-9-]*)*';
export const timePattern = `(?:\\d{1,2}:\\d{2}(?:am|pm)?|\\d{1,2}(?:am|pm))`;
export const timeRangePatternString = `\\b(${timePattern})\\s*-\\s*(${timePattern})\\b`;
export const singleTimePatternString = `(?<!!)\\b(${timePattern})(?!\\s*-)`;
export const durationPattern = '(?:\\d+h(?:\\s*\\d+m)?|\\d+m)';
// Reminder offsets additionally carry a day unit ("!2d before"); durations do not.
export const reminderOffsetPattern = `(?:\\d+d(?:\\s*\\d+h)?(?:\\s*\\d+m)?|${durationPattern})`;

export const pillSeparator = '\u00A0';
export const whitespace = '\u200B';

// Maps month names (and abbreviations) to their JavaScript Date object index (0-11).
export const MONTHS: Record<Locales, Record<string, number>> = {
  [Locales.EnUS]: {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  },
  [Locales.NlNL]: {
    jan: 0,
    januari: 0,
    feb: 1,
    februari: 1,
    mrt: 2,
    maart: 2,
    apr: 3,
    april: 3,
    mei: 4,
    jun: 5,
    juni: 5,
    jul: 6,
    juli: 6,
    aug: 7,
    augustus: 7,
    sep: 8,
    september: 8,
    okt: 9,
    oktober: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  },
  [Locales.EsES]: {
    ene: 0,
    enero: 0,
    feb: 1,
    febrero: 1,
    mar: 2,
    marzo: 2,
    abr: 3,
    abril: 3,
    may: 4,
    mayo: 4,
    jun: 5,
    junio: 5,
    jul: 6,
    julio: 6,
    ago: 7,
    agosto: 7,
    sep: 8,
    septiembre: 8,
    oct: 9,
    octubre: 9,
    nov: 10,
    noviembre: 10,
    dic: 11,
    diciembre: 11,
  },
  [Locales.DeDE]: {
    jan: 0,
    januar: 0,
    feb: 1,
    februar: 1,
    mär: 2,
    märz: 2,
    apr: 3,
    april: 3,
    mai: 4,
    jun: 5,
    juni: 5,
    jul: 6,
    juli: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    okt: 9,
    oktober: 9,
    nov: 10,
    november: 10,
    dez: 11,
    dezember: 11,
  },
};

// Maps day names to the RRULE standard two-letter abbreviation (e.g., MO, TU, WE).
export const dayMap: Record<Locales, Record<string, string>> = {
  [Locales.EnUS]: {
    mon: 'MO',
    monday: 'MO',
    tue: 'TU',
    tuesday: 'TU',
    wed: 'WE',
    wednesday: 'WE',
    thu: 'TH',
    thursday: 'TH',
    fri: 'FR',
    friday: 'FR',
    sat: 'SA',
    saturday: 'SA',
    sun: 'SU',
    sunday: 'SU',
  },
  [Locales.NlNL]: {
    ma: 'MO',
    maandag: 'MO',
    di: 'TU',
    dinsdag: 'TU',
    wo: 'WE',
    woensdag: 'WE',
    do: 'TH',
    donderdag: 'TH',
    vr: 'FR',
    vrijdag: 'FR',
    za: 'SA',
    zaterdag: 'SA',
    zo: 'SU',
    zondag: 'SU',
  },
  [Locales.EsES]: {
    lun: 'MO',
    lunes: 'MO',
    mar: 'TU',
    martes: 'TU',
    mié: 'WE',
    miércoles: 'WE',
    jue: 'TH',
    jueves: 'TH',
    vie: 'FR',
    viernes: 'FR',
    sáb: 'SA',
    sábado: 'SA',
    sábados: 'SA',
    dom: 'SU',
    domingo: 'SU',
    domingos: 'SU',
  },
  [Locales.DeDE]: {
    mo: 'MO',
    montag: 'MO',
    di: 'TU',
    dienstag: 'TU',
    mi: 'WE',
    mittwoch: 'WE',
    do: 'TH',
    donnerstag: 'TH',
    fr: 'FR',
    freitag: 'FR',
    sa: 'SA',
    samstag: 'SA',
    so: 'SU',
    sonntag: 'SU',
  },
};

// Keywords for parsing recurrence rules (RRULEs).
export const rruleKeywords: Record<Locales, Record<string, string>> = {
  [Locales.EnUS]: {
    every: 'every',
    every_simple: 'every',
    on: 'on',
    on_simple: 'on',
    the: 'the',
    in: 'in',
    in_simple: 'in',
    days: 'days',
    weeks: 'weeks',
    months: 'months',
    years: 'years',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
    weekday: 'weekday',
    weekday_simple: 'weekday',
    weekend: 'weekend',
    weekend_simple: 'weekend',
    daily: 'daily',
    weekly: 'weekly',
    monthly: 'monthly',
    yearly: 'yearly',
    ordinals: '(?:st|nd|rd|th)',
    first: 'first',
    first_simple: 'first',
    second: 'second',
    second_simple: 'second',
    third: 'third',
    third_simple: 'third',
    fourth: 'fourth',
    fourth_simple: 'fourth',
    fifth: 'fifth',
    fifth_simple: 'fifth',
    last: 'last',
    last_simple: 'last',
  },
  [Locales.NlNL]: {
    every: '(?:elk|elke)',
    every_simple: 'elke',
    on: 'op',
    on_simple: 'op',
    the: 'de',
    in: 'in',
    in_simple: 'in',
    days: 'dagen',
    weeks: 'weken',
    months: 'maanden',
    years: 'jaar',
    day: 'dag',
    week: 'week',
    month: 'maand',
    year: 'jaar',
    weekday: 'weekdag',
    weekday_simple: 'weekdag',
    weekend: 'weekend',
    weekend_simple: 'weekend',
    daily: 'dagelijks',
    weekly: 'wekelijks',
    monthly: 'maandelijks',
    yearly: 'jaarlijks',
    ordinals: '(?:e|ste)',
    first: 'eerste',
    first_simple: 'eerste',
    second: 'tweede',
    second_simple: 'tweede',
    third: 'derde',
    third_simple: 'derde',
    fourth: 'vierde',
    fourth_simple: 'vierde',
    fifth: 'vijfde',
    fifth_simple: 'vijfde',
    last: 'laatste',
    last_simple: 'laatste',
  },
  [Locales.EsES]: {
    every: '(?:cada|todos los|todas las)',
    every_simple: 'cada',
    on: '(?:el|los)',
    on_simple: 'el',
    the: '(?:el|la|los|las)',
    in: '(?:en|de)',
    in_simple: 'en',
    days: 'días',
    weeks: 'semanas',
    months: 'meses',
    years: 'años',
    day: 'día',
    week: 'semana',
    month: 'mes',
    year: 'año',
    weekday: 'd[íi]a(?:s)? de (?:la )?semana',
    weekday_simple: 'día de la semana',
    weekend: 'fin(?:es)? de semana',
    weekend_simple: 'fin de semana',
    daily: 'diariamente',
    weekly: 'semanalmente',
    monthly: 'mensualmente',
    yearly: 'anualmente',
    ordinals: '(?:º|ª|er)',
    first: '(?:primer|primero)',
    first_simple: 'primero',
    second: 'segundo',
    second_simple: 'segundo',
    third: '(?:tercer|tercero)',
    third_simple: 'tercero',
    fourth: 'cuarto',
    fourth_simple: 'cuarto',
    fifth: 'quinto',
    fifth_simple: 'quinto',
    last: 'último',
    last_simple: 'último',
  },
  [Locales.DeDE]: {
    every: '(?:jeden|jede|jedes|alle)',
    every_simple: 'jeden',
    on: 'am',
    on_simple: 'am',
    the: '(?:den|dem|der)',
    in: 'im',
    in_simple: 'im',
    und: 'und',
    days: 'tage',
    weeks: 'wochen',
    months: 'monate',
    years: 'jahre',
    day: 'tag',
    week: 'woche',
    month: 'monat',
    year: 'jahr',
    weekday: 'wochentag',
    weekday_simple: 'wochentag',
    weekend: 'wochenende',
    weekend_simple: 'wochenende',
    daily: 'täglich',
    weekly: 'wöchentlich',
    monthly: 'monatlich',
    yearly: 'jährlich',
    ordinals: '(?:\\.|te|ter|tes)',
    first: 'ersten',
    first_simple: 'ersten',
    second: 'zweiten',
    second_simple: 'zweiten',
    third: 'dritten',
    third_simple: 'dritten',
    fourth: 'vierten',
    fourth_simple: 'vierten',
    fifth: 'fünften',
    fifth_simple: 'fünften',
    last: 'letzten',
    last_simple: 'letzten',
  },
};
